import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import styled from 'styled-components';
import { FUTURE_TOKENS } from '../../constants/futureTokens.js';
import { getDropShadowByElevation } from '../../styles/elevation.js';

var base = {
  alignSelf: "flex-start",
  cursor: "pointer",
  display: "inline-block",
  fontWeight: 600,
  letterSpacing: "inherit",
  maxWidth: "100%",
  outline: "none",
  padding: 0,
  position: "relative",
  userSelect: "none",
  WebkitTapHighlightColor: "transparent",
  whiteSpace: "nowrap"
};
function getColorsByVariant() {
  var variant = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "secondary";
  var theme = arguments.length > 1 ? arguments[1] : undefined;
  var error = theme.alertColors.error,
    _theme$CTAColors = theme.CTAColors,
    primary = _theme$CTAColors.primary,
    primaryBg = _theme$CTAColors.primaryBg,
    primaryVariant = _theme$CTAColors.primaryVariant,
    _theme$neutralColors = theme.neutralColors,
    charcoal = _theme$neutralColors.charcoal,
    pearl = _theme$neutralColors.pearl,
    rhino = _theme$neutralColors.rhino,
    silver = _theme$neutralColors.silver,
    smoke = _theme$neutralColors.smoke,
    white = _theme$neutralColors.white;
  var colorsByVariant = {
    primary: {
      bgColor: primary,
      bgColorActive: void 0,
      bgColorHover: primaryVariant,
      borderColor: primary,
      borderColorActive: void 0,
      borderColorHover: primaryVariant,
      color: white,
      colorActive: void 0,
      colorHover: white,
      shadow: void 0,
      shadowHover: void 0
    },
    invertedPrimary: {
      bgColor: white,
      bgColorActive: pearl,
      bgColorHover: void 0,
      borderColor: rhino,
      borderColorActive: rhino,
      borderColorHover: silver,
      color: charcoal,
      colorActive: void 0,
      colorHover: charcoal,
      shadow: void 0,
      shadowHover: void 0
    },
    secondary: {
      bgColor: white,
      bgColorActive: primaryBg,
      bgColorHover: white,
      borderColor: silver,
      borderColorActive: primary,
      borderColorHover: primaryVariant,
      color: primary,
      colorActive: primary,
      colorHover: primaryVariant,
      shadow: void 0,
      shadowHover: void 0
    },
    invertedSecondary: {
      bgColor: charcoal,
      bgColorActive: charcoal,
      bgColorHover: charcoal,
      borderColor: silver,
      borderColorActive: white,
      borderColorHover: white,
      color: silver,
      colorActive: white,
      colorHover: white,
      shadow: void 0,
      shadowHover: void 0
    },
    tertiary: {
      bgColor: white,
      bgColorActive: FUTURE_TOKENS.BUTTON_TERTIARY_VARIANT_ACTIVE_COLOR,
      bgColorHover: void 0,
      borderColor: silver,
      borderColorActive: charcoal,
      borderColorHover: charcoal,
      color: charcoal,
      colorActive: void 0,
      colorHover: void 0,
      shadow: void 0,
      shadowHover: void 0
    },
    ghost: {
      bgColor: "transparent",
      bgColorActive: FUTURE_TOKENS.BUTTON_GHOST_VARIANT_ACTIVE_COLOR,
      bgColorHover: FUTURE_TOKENS.BUTTON_GHOST_VARIANT_HOVER_COLOR,
      borderColor: void 0,
      borderColorActive: void 0,
      borderColorHover: void 0,
      color: smoke,
      colorActive: void 0,
      colorHover: void 0,
      shadow: void 0,
      shadowHover: void 0
    },
    invertedGhost: {
      bgColor: "transparent",
      bgColorActive: "transparent",
      bgColorHover: "transparent",
      borderColor: void 0,
      borderColorActive: white,
      borderColorHover: white,
      color: silver,
      colorActive: white,
      colorHover: white,
      shadow: void 0,
      shadowHover: void 0
    },
    warning: {
      bgColor: error,
      bgColorActive: FUTURE_TOKENS.BUTTON_ERROR_VARIANT_ACTIVE_COLOR,
      bgColorHover: FUTURE_TOKENS.BUTTON_ERROR_VARIANT_HOVER_COLOR,
      borderColor: error,
      borderColorActive: charcoal,
      borderColorHover: FUTURE_TOKENS.BUTTON_ERROR_VARIANT_HOVER_COLOR,
      color: white,
      colorActive: void 0,
      colorHover: void 0,
      shadow: void 0,
      shadowHover: void 0
    },
    tag: {
      bgColor: "#ECEEF0",
      bgColorActive: silver,
      bgColorHover: silver,
      borderColor: "transparent",
      borderColorActive: void 0,
      borderColorHover: void 0,
      color: charcoal,
      colorActive: void 0,
      colorHover: void 0,
      shadow: void 0,
      shadowHover: void 0
    },
    tagSuccess: {
      bgColor: FUTURE_TOKENS.BUTTON_TAG_SUCCESS_BACKGROUND_COLOR,
      bgColorActive: FUTURE_TOKENS.BUTTON_TAG_SUCCESS_BACKGROUND_ACTIVE_COLOR,
      bgColorHover: FUTURE_TOKENS.BUTTON_TAG_SUCCESS_BACKGROUND_HOVER_COLOR,
      borderColor: "transparent",
      borderColorActive: void 0,
      borderColorHover: void 0,
      color: charcoal,
      colorActive: void 0,
      colorHover: void 0,
      shadow: void 0,
      shadowHover: void 0
    },
    tagDanger: {
      bgColor: FUTURE_TOKENS.BUTTON_TAG_DANGER_BACKGROUND_COLOR,
      bgColorActive: FUTURE_TOKENS.BUTTON_TAG_DANGER_BACKGROUND_HOVER_COLOR,
      bgColorHover: FUTURE_TOKENS.BUTTON_TAG_DANGER_BACKGROUND_HOVER_COLOR,
      borderColor: "transparent",
      borderColorActive: void 0,
      borderColorHover: void 0,
      color: charcoal,
      colorActive: void 0,
      colorHover: void 0,
      shadow: void 0,
      shadowHover: void 0
    }
  };
  return colorsByVariant[variant] || colorsByVariant.secondary;
}
function getStylesByVariant(variant, theme) {
  var _getColorsByVariant = getColorsByVariant(variant, theme),
    bgColor = _getColorsByVariant.bgColor,
    bgColorActive = _getColorsByVariant.bgColorActive,
    bgColorHover = _getColorsByVariant.bgColorHover,
    borderColor = _getColorsByVariant.borderColor,
    borderColorActive = _getColorsByVariant.borderColorActive,
    borderColorHover = _getColorsByVariant.borderColorHover,
    color = _getColorsByVariant.color,
    colorActive = _getColorsByVariant.colorActive,
    colorHover = _getColorsByVariant.colorHover;
  var hasNoActiveState = !bgColorActive && !borderColorActive && !colorActive;
  var styles = {
    backgroundColor: bgColor,
    borderColor: borderColor || "transparent",
    color: color
  };
  if (hasNoActiveState) {
    styles[":hover, :focus, :active, &._mntl-active"] = {
      backgroundColor: bgColorHover,
      borderColor: borderColorHover || "transparent",
      color: colorHover,
      textDecoration: "none",
      transition: "background-color 0.25s, border-color 0.25s"
    };
  } else {
    styles[":hover, :focus"] = {
      backgroundColor: bgColorHover,
      borderColor: borderColorHover || "transparent",
      color: colorHover,
      textDecoration: "none",
      transition: "background-color 0.25s, border-color 0.25s, color 0.25s"
    };
    styles[":active, &._mntl-active"] = {
      backgroundColor: bgColorActive,
      borderColor: borderColorActive || "transparent",
      color: colorActive,
      textDecoration: "none",
      transition: "border-color 0.25s, color 0.25s"
    };
  }
  return styles;
}
var StyledButtonBase = styled.button.withConfig({
  displayName: "StyledButton__StyledButtonBase",
  componentId: "sc-1fmfard-0"
})(function (_ref) {
  var theme = _ref.theme;
  var _theme$neutralColors2 = theme.neutralColors,
    charcoal = _theme$neutralColors2.charcoal,
    rhino = _theme$neutralColors2.rhino,
    silver = _theme$neutralColors2.silver,
    white = _theme$neutralColors2.white;
  var boxShadow = getDropShadowByElevation(1);
  var boxShadowHover = getDropShadowByElevation(2);
  return {
    "&&": _objectSpread(_objectSpread({}, base), {}, {
      borderColor: "transparent",
      borderRadius: 4,
      borderStyle: "solid",
      borderWidth: 1,
      fontSize: 14,
      lineHeight: "16px",
      minHeight: 32,
      minWidth: 32,
      padding: "0px 15px",
      ":focus": {
        "::before": {
          content: "''",
          position: "absolute",
          top: "-3px",
          right: "-3px",
          bottom: "-3px",
          left: "-3px",
          border: "1px solid",
          borderColor: charcoal,
          borderRadius: "6px"
        },
        "::after": {
          content: "''",
          position: "absolute",
          top: "-4px",
          right: "-4px",
          bottom: "-4px",
          left: "-4px",
          border: "1px solid",
          borderColor: white,
          borderRadius: "7px"
        }
      },
      "&[class*=ButtonGroupItem__ItemButton]": {
        ":focus": {
          "::before": {
            top: "0px",
            right: "0px",
            bottom: "0px",
            left: "0px",
            borderRadius: "3px"
          },
          "::after": {
            borderColor: "transparent"
          }
        }
      }
    }),
    "&._mntl-hideLabel": {
      padding: "7px"
    },
    "&._mntl-elevated": {
      boxShadow: boxShadow,
      ":hover": {
        boxShadow: boxShadowHover
      }
    },
    "&._mntl-tag, &._mntl-tagSuccess, &._mntl-tagDanger": {
      fontSize: 12,
      minHeight: 24,
      minWidth: 24,
      padding: "0px 8px",
      "&&": {
        "&._mntl-anchor-tag": {
          "&:hover, &:focus, &:active, &._mntl-active": {
            textDecoration: "underline",
            color: charcoal
          }
        },
        "[class^=A11YLabel__LabelWrapper]": {
          height: 22
        }
      }
    },
    "&&._mntl-disabled": {
      backgroundColor: white,
      color: rhino,
      cursor: "not-allowed",
      ":hover, :active, :focus": {
        color: rhino,
        outline: "none",
        backgroundColor: white
      },
      "&._mntl-secondary, &._mntl-tertiary": {
        borderColor: silver
      },
      "&._mntl-primary, &._mntl-warning, &._mntl-tag, &._mntl-tagSuccess, &._mntl-tagDanger": {
        ":hover": {
          backgroundColor: silver
        },
        backgroundColor: silver,
        borderColor: "transparent"
      },
      "&._mntl-elevated": {
        ":hover": {
          boxShadow: boxShadow
        }
      }
    }
  };
});
var StyledButton = styled(StyledButtonBase).withConfig({
  displayName: "StyledButton",
  componentId: "sc-1fmfard-1"
})(function (_ref2) {
  var theme = _ref2.theme,
    variant = _ref2.variant;
  return _defineProperty({}, "&._mntl-".concat(variant), getStylesByVariant(variant, theme));
});

export { StyledButton, base, getColorsByVariant };
